/**
 * CanonicalMoneyStates — the ONE money panel. Both the Commercial Dashboard funnel
 * and the Financials page render this, sourced from GET /api/finance/summary, so the
 * two screens cannot disagree.
 *
 * The pipeline is shown as a STAGE LADDER, never a weighted forecast: no figure here is
 * multiplied by a stage probability. Each live stage shows its deal count, how many carry
 * a value, and the amount per currency (never converted). Above the ladder sits the plain
 * face value of the open book. Contracted / Invoiced / Collected remain facts, unchanged.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Pause } from "lucide-react";

const _API_BASE = (typeof window !== "undefined" && window.location.hostname !== "localhost")
  ? window.location.origin
  : (process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : ""));
const API = `${_API_BASE}/api`;

const fmt = (n, ccy) => {
  if (n == null || isNaN(n)) return "—";
  const p = ccy ? `${ccy} ` : "";
  if (Math.abs(n) >= 1e6) return `${p}${(n / 1e6).toFixed(2)}M`;
  if (Math.abs(n) >= 1e3) return `${p}${(n / 1e3).toFixed(0)}k`;
  return `${p}${n.toFixed(0)}`;
};

// Full, exact amount (thousands-separated) — the ladder shows face value precisely,
// per currency, never abbreviated into a single mixed number.
const fmtFull = (n, ccy) => `${ccy && ccy !== "__NO_CURRENCY__" ? ccy + " " : ""}${Math.round(n).toLocaleString()}`;

const perCcy = (by) => Object.entries(by || {})
  .filter(([k]) => k !== "__NO_CURRENCY__" && k !== "__error__")
  .map(([k, v]) => `${fmt(v, k)}`).join(" · ") || "—";

// list every currency a stage/total holds, exactly, never summed across currencies
const perCcyFull = (by) => {
  const items = Object.entries(by || {}).filter(([k]) => k !== "__error__");
  if (!items.length) return "none recorded";
  return items.map(([k, v]) => fmtFull(v, k === "__NO_CURRENCY__" ? "(no currency)" : k)).join("  ·  ");
};

const STATES = [
  { key: "contracted", label: "Contracted", tag: "signed, not cash",
    tone: "border-blue-200 bg-blue-50 text-blue-900", countKey: "deal_count", countWord: "deal" },
  { key: "invoiced", label: "Invoiced", tag: "chaseable / ageable",
    tone: "border-amber-200 bg-amber-50 text-amber-900", countKey: "invoice_count", countWord: "invoice" },
  { key: "collected", label: "Collected", tag: "received — the only real money",
    tone: "border-emerald-200 bg-emerald-50 text-emerald-900", countKey: "payment_count", countWord: "payment" },
];

function StageLadder({ ladder }) {
  const navigate = useNavigate();
  if (!ladder) return null;
  const rows = ladder.rows || [];
  const face = ladder.face_value?.by_currency || {};
  const onHold = ladder.on_hold_excluded || { count: 0, by_currency: {} };
  const contradictions = ladder.contradictions || [];

  const openContradiction = (c) => {
    if (!c.client_id) return;
    navigate(`/clients/${c.client_id}?tab=opportunities&focus=${encodeURIComponent(c.opportunity_id || "")}`);
  };

  return (
    <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-3 mb-3" data-testid="money-stage-ladder">
      {/* Plain total — the face value of the open book, explicitly NOT a forecast */}
      <div className="flex items-baseline justify-between flex-wrap gap-x-3 gap-y-1 mb-2">
        <div>
          <p className="text-[10px] uppercase tracking-wider font-semibold text-indigo-900/80">Open book — face value</p>
          <p className="text-[9px] uppercase tracking-wide text-indigo-900/60">plain sum of live stages · not a forecast · not expected cash</p>
        </div>
        <p className="text-base font-black text-indigo-900 leading-tight" data-testid="ladder-face-value">{perCcyFull(face)}</p>
      </div>
      {onHold.count > 0 && (
        <p className="text-[10px] text-indigo-900/60 mb-2" data-testid="ladder-onhold-note">
          Excludes {onHold.count} On Hold deal{onHold.count === 1 ? "" : "s"}
          {Object.keys(onHold.by_currency || {}).length ? ` (${perCcyFull(onHold.by_currency)})` : ""}, still shown in their rows below.
        </p>
      )}

      {/* The ladder: one row per live stage */}
      <div className="rounded-md border border-indigo-100 overflow-x-auto bg-white">
        <table className="w-full text-xs min-w-[320px]">
          <thead>
            <tr className="bg-indigo-100/50 text-indigo-900/70 text-[10px] uppercase tracking-wide">
              <th className="text-left font-semibold px-2 py-1">Stage</th>
              <th className="text-right font-semibold px-2 py-1">Deals</th>
              <th className="text-right font-semibold px-2 py-1">Valued</th>
              <th className="text-right font-semibold px-2 py-1">Amount (face value, per currency)</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={4} className="px-2 py-2 text-slate-400 text-center">No live deals.</td></tr>
            )}
            {rows.map((r) => {
              const slug = r.stage.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
              return (
                <tr key={r.stage} className="border-t border-indigo-50" data-testid={`ladder-row-${slug}`}>
                  <td className="px-2 py-1 text-slate-800">
                    {r.stage}
                    {r.on_hold_count > 0 && (
                      <span className="ml-1.5 inline-flex items-center gap-0.5 rounded bg-slate-100 text-slate-500 px-1 py-0.5 text-[9px] align-middle" data-testid={`ladder-onhold-${slug}`}>
                        <Pause className="w-2.5 h-2.5" />{r.on_hold_count} on hold · out of total
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums text-slate-700" data-testid={`ladder-deals-${slug}`}>{r.deal_count}</td>
                  <td className="px-2 py-1 text-right tabular-nums text-slate-700" data-testid={`ladder-valued-${slug}`}>{r.valued_count}</td>
                  <td className="px-2 py-1 text-right tabular-nums text-slate-900 font-medium" data-testid={`ladder-amount-${slug}`}>{perCcyFull(r.by_currency)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {contradictions.length > 0 && (
        <div className="mt-2 rounded-md border border-rose-300 bg-rose-50 p-2 text-[11px] text-rose-800" data-testid="ladder-contradictions">
          <div className="font-semibold flex items-center gap-1 mb-1">
            <AlertTriangle className="w-3.5 h-3.5" />
            {contradictions.length} record{contradictions.length === 1 ? "" : "s"} need correction — status and stage disagree (not counted above)
          </div>
          <ul className="space-y-0.5">
            {contradictions.map((c, i) => (
              <li key={c.opportunity_id || i} data-testid={`ladder-contradiction-${i}`}>
                <button
                  type="button"
                  onClick={() => openContradiction(c)}
                  disabled={!c.client_id}
                  data-testid={`ladder-contradiction-link-${i}`}
                  className={`w-full text-left rounded px-1 py-0.5 -mx-1 transition-colors ${c.client_id ? "hover:bg-rose-100 cursor-pointer" : "cursor-default"}`}
                  title={c.client_id ? "Open this client and fix the deal" : undefined}
                >
                  <span className="font-medium">{c.company_name || "Unnamed client"}</span>
                  {c.opportunity_name ? <span className="text-rose-700"> · {c.opportunity_name}</span> : ""}
                  <span className="text-rose-800"> — {c.status} at “{c.stage}”</span>
                  {c.value != null ? <span className="text-rose-700/80"> · {fmtFull(c.value, c.currency)}</span> : ""}
                  {c.opportunity_id ? <span className="ml-1 text-[9px] text-rose-400 tabular-nums align-middle">{c.opportunity_id}</span> : ""}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function CanonicalMoneyStates({ compact = false }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    fetch(`${API}/finance/summary`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => { if (live) setData(d); })
      .catch((e) => { if (live) setError(e.message); });
    return () => { live = false; };
  }, []);

  if (error) return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-500" data-testid="money-states-error">
      Money summary unavailable ({error}).
    </div>
  );
  if (!data) return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 animate-pulse h-24" data-testid="money-states-loading" />
  );

  const rates = data.fx_rates_to_base || {};
  const base = (data.contracted?.converted?.base_currency) || "NGN";
  const ratesLine = Object.entries(rates).filter(([k]) => k !== base)
    .map(([k, v]) => `1 ${k} = ${Number(v).toLocaleString()} ${base}`).join(", ");

  // aggregate anything unconvertible across the converted fact-states, for one honest disclosure
  const unconv = {};
  ["contracted", "invoiced"].forEach((k) => {
    const u = data[k]?.converted?.unconvertible || {};
    Object.entries(u).forEach(([c, amt]) => { unconv[c] = (unconv[c] || 0) + amt; });
  });
  const unconvItems = Object.entries(unconv);

  return (
    <div data-testid="canonical-money-states">
      {/* Pipeline state — a stage ladder, never a weighted forecast */}
      <StageLadder ladder={data.stage_ladder} />

      {/* The facts: contracted, invoiced, collected */}
      <div className={`grid gap-2 ${compact ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-1 sm:grid-cols-3"}`}>
        {STATES.map((s) => {
          const st = data[s.key] || {};
          const conv = st.converted;
          const count = st[s.countKey];
          return (
            <div key={s.key} className={`rounded-lg border p-3 ${s.tone}`} data-testid={`money-state-${s.key}`}>
              <p className="text-[10px] uppercase tracking-wider font-semibold opacity-80">{s.label}</p>
              <p className="text-[9px] uppercase tracking-wide opacity-70 mb-1">{s.tag}</p>
              {conv ? (
                <p className="text-lg font-black leading-tight" data-testid={`money-state-${s.key}-base`}>{fmt(conv.total, base)}</p>
              ) : (
                <p className="text-lg font-black leading-tight" data-testid={`money-state-${s.key}-perccy`}>{perCcy(st.by_currency)}</p>
              )}
              <p className="text-[10px] opacity-75 mt-0.5" data-testid={`money-state-${s.key}-detail`}>
                {conv ? `by currency: ${perCcy(st.by_currency)}` : "shown in the currency received; never converted"}
              </p>
              {count != null && (
                <p className="text-[10px] opacity-60 mt-0.5">{count} {s.countWord}{count === 1 ? "" : "s"}</p>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-[10px] text-slate-500 mt-2" data-testid="money-states-basis">
        Pipeline shown as a stage ladder at face value — never weighted by probability, amounts in the currency recorded and never converted. Contracted and invoiced converted to {base} at the working rates{ratesLine ? ` (${ratesLine})` : ""}. Collected is shown in the currency it arrived in and is never converted. Single source: /api/finance/summary.
      </p>
      {unconvItems.length > 0 && (
        <div className="mt-1.5 rounded-md border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-800 flex items-start gap-1.5" data-testid="money-states-unconvertible">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>These amounts are left OUT of the converted totals because there is no working rate: {unconvItems.map(([c, a]) => `${c} (${fmt(a, c === "(no currency)" ? "" : c)})`).join(", ")}. Set a rate to include them.</span>
        </div>
      )}
    </div>
  );
}
